import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';
import { showToast } from '@/services/toast.service';
import TenantConfigService from '@/services/tenantConfig.service';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import {
  DATE_RANGE_OPTIONS,
  FISCAL_DATE_RANGE_OPTIONS,
  mapDateRangeToBackend,
  mapDateRangeFromBackend,
} from '@/components/ui/date-range-picker';

const ALL_OPTIONS = [...DATE_RANGE_OPTIONS, ...FISCAL_DATE_RANGE_OPTIONS];

export default function AnalyticsTab() {
  const queryClient = useQueryClient();
  const { data: tenantConfig, isLoading } = useTenantConfig();
  const [saving, setSaving] = useState(false);
  const [savingSupersets, setSavingSupersets] = useState(false);
  const [supersetsEnabled, setSupersetsEnabled] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set());
  const [userFilterStrict, setUserFilterStrict] = useState(true);
  const [savingStrictMode, setSavingStrictMode] = useState(false);

  useEffect(() => {
    if (tenantConfig) {
      setSupersetsEnabled(!!tenantConfig.dashboard_supersets_enabled);
      const whitelist = tenantConfig.dashboard_date_range_options;
      if (whitelist && whitelist.length > 0) {
        setEnabled(true);
        const frontendValues = whitelist
          .map((v) => mapDateRangeFromBackend(v))
          .filter(Boolean) as string[];
        setSelectedOptions(new Set(frontendValues));
      } else {
        setEnabled(false);
        setSelectedOptions(new Set());
      }
      setUserFilterStrict(tenantConfig.user_filter_strict_mode ?? true);
    }
  }, [tenantConfig]);

  const handleToggleOption = useCallback((value: string, checked: boolean) => {
    setSelectedOptions((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(value);
      } else {
        next.delete(value);
      }
      return next;
    });
  }, []);

  const handleSaveSupersets = useCallback(async () => {
    if (!tenantConfig?.id) return;
    setSavingSupersets(true);
    try {
      await TenantConfigService.getInstance().saveDashboardSupersetsEnabled(
        tenantConfig.id,
        supersetsEnabled
      );
      showToast.success('Dashboard supersets setting saved');
      queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
    } catch (err: any) {
      showToast.error('Failed to save setting', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setSavingSupersets(false);
    }
  }, [tenantConfig?.id, supersetsEnabled, queryClient]);

  const handleSave = useCallback(async () => {
    if (!tenantConfig?.id) return;

    setSaving(true);
    try {
      let options: string[] | null = null;
      if (enabled && selectedOptions.size > 0) {
        options = Array.from(selectedOptions).map((v) => mapDateRangeToBackend(v));
      }
      await TenantConfigService.getInstance().saveDashboardDateRangeOptions(
        tenantConfig.id,
        options
      );
      showToast.success('Analytics settings saved');
      queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
    } catch (err: any) {
      showToast.error('Failed to save analytics settings', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setSaving(false);
    }
  }, [tenantConfig?.id, enabled, selectedOptions, queryClient]);

  const handleSaveStrictMode = useCallback(async (strict: boolean) => {
    if (!tenantConfig?.id) return;

    setSavingStrictMode(true);
    try {
      await TenantConfigService.getInstance().saveUserFilterStrictMode(
        tenantConfig.id,
        strict
      );
      setUserFilterStrict(strict);
      showToast.success('User filter setting saved');
      queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
    } catch (err: any) {
      showToast.error('Failed to save user filter setting', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setSavingStrictMode(false);
    }
  }, [tenantConfig?.id, queryClient]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Dashboard Supersets</CardTitle>
          <CardDescription>
            When enabled, dashboard queries are combined into shared superset datasets for faster load times.
            Dashboards will fall back to per-chart queries when disabled.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="enable-dashboard-supersets"
              checked={supersetsEnabled}
              onCheckedChange={(checked) => setSupersetsEnabled(checked)}
            />
            <Label htmlFor="enable-dashboard-supersets">
              Enable dashboard supersets
            </Label>
          </div>
          <Button onClick={handleSaveSupersets} disabled={savingSupersets}>
            {savingSupersets ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Dashboard Date Range Options</CardTitle>
          <CardDescription>
            Restrict which date range options are available in dashboards and chart editors.
            When enabled, only the selected options will appear in date range pickers.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="enable-date-range-whitelist"
              checked={enabled}
              onCheckedChange={(checked) => {
                setEnabled(checked === true);
                if (!checked) {
                  setSelectedOptions(new Set());
                }
              }}
            />
            <Label htmlFor="enable-date-range-whitelist">
              Limit available date range options
            </Label>
          </div>

          {enabled && (
            <div className="space-y-3 pl-6 border-l-2 border-muted ml-2">
              <p className="text-sm text-muted-foreground">
                Select the date range options that should be available:
              </p>
              <div className="grid grid-cols-2 gap-2">
                {DATE_RANGE_OPTIONS.map((option) => (
                  <div key={option.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`dr-${option.value}`}
                      checked={selectedOptions.has(option.value)}
                      onCheckedChange={(checked) =>
                        handleToggleOption(option.value, checked === true)
                      }
                    />
                    <Label htmlFor={`dr-${option.value}`} className="text-sm font-normal">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>

              {FISCAL_DATE_RANGE_OPTIONS.length > 0 && (
                <>
                  <p className="text-sm text-muted-foreground mt-4">Fiscal Periods</p>
                  <div className="grid grid-cols-2 gap-2">
                    {FISCAL_DATE_RANGE_OPTIONS.map((option) => (
                      <div key={option.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`dr-${option.value}`}
                          checked={selectedOptions.has(option.value)}
                          onCheckedChange={(checked) =>
                            handleToggleOption(option.value, checked === true)
                          }
                        />
                        <Label htmlFor={`dr-${option.value}`} className="text-sm font-normal">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {enabled && selectedOptions.size === 0 && (
                <p className="text-sm text-destructive">
                  Select at least one option, or disable the restriction.
                </p>
              )}
            </div>
          )}

          <Button onClick={handleSave} disabled={saving || (enabled && selectedOptions.size === 0)}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>User Permission Filters</CardTitle>
          <CardDescription>
            Control what happens when a user has a permission-level dimension filter
            that doesn't apply to a particular metric on a chart.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="user-filter-strict-mode">Strict mode</Label>
              <p className="text-sm text-muted-foreground">
                When enabled, charts will show an error if a user's permission filter
                references a dimension that a metric cannot be broken down by. When
                disabled, the filter is silently skipped and the chart renders without it.
              </p>
            </div>
            <Switch
              id="user-filter-strict-mode"
              checked={userFilterStrict}
              disabled={savingStrictMode}
              onCheckedChange={(checked) => handleSaveStrictMode(checked)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
